import type {
    DeliveryTimeslot,
    EnterPostCodeEvent,
    SelectEditOrderTimeEvent,
    SelectNewTimeEvent,
    SelectPickupStoreEvent,
    SelectTimeEvent,
    TimeLostEvent,
    TimeslotSource,
} from './ga4Cnc';

import { failSilently } from '../../utils/failSilently';
import { mapDateRangeToGA, mapDateToGA, mapDateWeekdayToGA } from '../shared/ga4SharedMappers';
import { sendGA4Event } from '../shared/sendGa4Event';

const buildGaDeliveryTimeslot = (fromDate: Date, toDate: Date) => {
    const timeslot: DeliveryTimeslot = {
        delivery_weekday: mapDateWeekdayToGA(fromDate),
        delivery_date: mapDateToGA(fromDate),
        time_slot: mapDateRangeToGA(fromDate, toDate),
    };
    return timeslot;
};

const enterPostcode = (postalCode: string, source: 'input' | 'address') => {
    let event: EnterPostCodeEvent | null = null;

    switch (source) {
        case 'address':
            event = {
                event: 'enter_postcode',
                feature_name: 'previous address',
                type: 'add',
                postcode: postalCode,
                event_key: 'enter_postcode_previous_address_add',
            };
            break;
        case 'input':
            event = {
                event: 'enter_postcode',
                feature_name: 'post code slider',
                type: 'add',
                postcode: postalCode,
                event_key: 'enter_postcode_post_code_slider_add',
            };
            break;
        default:
            return;
    }

    sendGA4Event(event);
};

const selectPickupStore = (storeId: string, storeName: string) => {
    const event: SelectPickupStoreEvent = {
        event: 'select_delivery',
        event_key: 'select_delivery_select_store',
        type: 'select store',
        shipping_tier: 'store pickup',
        store_id: storeId,
        store_name: storeName.toLowerCase(),
    };

    sendGA4Event(event);
};

const selectTimePickupDelivery = (
    fromDate: Date,
    toDate: Date,
    storeId: string | null,
    storeName: string | null,
    section: TimeslotSource,
) => {
    const timeslot = buildGaDeliveryTimeslot(fromDate, toDate);

    const event: SelectTimeEvent = {
        event: 'select_delivery',
        type: 'select time',
        event_key: 'select_delivery_select_time',
        section,
        ...timeslot,
        shipping_tier: 'store pickup',
        store_id: storeId,
        store_name: storeName?.toLowerCase() || null,
    };

    sendGA4Event(event);
};

const selectTimeHomeDelivery = (
    fromDate: Date,
    toDate: Date,
    postalCode: string | undefined,
    section: TimeslotSource,
) => {
    const timeslot = buildGaDeliveryTimeslot(fromDate, toDate);

    const event: SelectTimeEvent = {
        event: 'select_delivery',
        type: 'select time',
        event_key: 'select_delivery_select_time',
        section,
        ...timeslot,
        shipping_tier: 'home delivery',
        postcode: postalCode,
    };

    sendGA4Event(event);
};

const selectTimeEditOrder = (action: 'start' | 'confirm') => {
    const event: SelectEditOrderTimeEvent = {
        event: 'edit_order_time',
        event_action: action,
    };

    sendGA4Event(event);
};

const selectNewTimePickupDelivery = (
    fromDate: Date,
    toDate: Date,
    storeId: string | null,
    storeName: string | null,
    section: TimeslotSource,
) => {
    const timeslot = buildGaDeliveryTimeslot(fromDate, toDate);

    const event: SelectNewTimeEvent = {
        event: 'select_delivery',
        type: 'select new time',
        event_key: 'select_delivery_select_new_time',
        section,
        ...timeslot,
        shipping_tier: 'store pickup',
        store_id: storeId,
        store_name: storeName?.toLowerCase() || null,
    };

    sendGA4Event(event);
};

const selectNewTimeHomeDelivery = (
    fromDate: Date,
    toDate: Date,
    postalCode: string | undefined,
    section: TimeslotSource,
) => {
    const timeslot = buildGaDeliveryTimeslot(fromDate, toDate);

    const event: SelectNewTimeEvent = {
        event: 'select_delivery',
        type: 'select new time',
        event_key: 'select_delivery_select_new_time',
        section,
        ...timeslot,
        shipping_tier: 'home delivery',
        postcode: postalCode,
    };

    sendGA4Event(event);
};

const timeLostPickupDelivery = (storeId: string, storeName: string) => {
    const event: TimeLostEvent = {
        event: 'select_delivery',
        event_key: 'select_delivery_time_lost',
        type: 'time lost',
        shipping_tier: 'store pickup',
        store_id: storeId,
        store_name: storeName.toLowerCase(),
    };

    sendGA4Event(event);
};

const timeLostHomeDelivery = (postalCode: string) => {
    const event: TimeLostEvent = {
        event: 'select_delivery',
        event_key: 'select_delivery_time_lost',
        type: 'time lost',
        shipping_tier: 'home delivery',
        postcode: postalCode,
    };

    sendGA4Event(event);
};

export default {
    enterPostcode: failSilently(enterPostcode),
    selectPickupStore: failSilently(selectPickupStore),
    selectTime: {
        home: failSilently(selectTimeHomeDelivery),
        pickup: failSilently(selectTimePickupDelivery),
        editOrder: failSilently(selectTimeEditOrder),
    },
    selectNewTime: {
        home: failSilently(selectNewTimeHomeDelivery),
        pickup: failSilently(selectNewTimePickupDelivery),
    },
    timeLost: {
        pickup: failSilently(timeLostPickupDelivery),
        home: failSilently(timeLostHomeDelivery),
    },
};
